<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { $router } from '@/router'
import { $bus } from '@/common/events'
import { dialog } from '@/common/dialog'
import { useTitle } from '@/composables/title'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import {
  useVirtualKeyboard,
  onVirtualKeyPress,
  onVirtualKeyDown,
  onVirtualKeyUp,
  onVirtualKeyboardShow,
  onVirtualKeyboardHide,
} from '@/composables/keyboard'
import TextBoxTouchFriendly from '@/components/TextBoxTouchFriendly.vue'
import TextAreaTouchFriendly from '@/components/TextAreaTouchFriendly.vue'
import KeyboardMacbook from '@/components/KeyboardMacbook.vue'

const text1 = ref("");
const text2 = ref("");
const text3 = ref("");
const text4 = ref("");
const text5 = ref("");
const search = ref("");
const lastKey = ref("");
const keyState = computed(() => {
  if (isKeyDown.value === true) {
    return "down";
  }
  return null;
});
const isKeyDown = ref(null);

const keyboardTypes = ref([
  { id: "default", name: "Default" },
  { id: "numeric", name: "Numeric" },
]);

const {
  vkeyboard,
  keyboardType,
  keyboardLayout,
  isKeyboardReady,
  isKeyboardEnabled,
  isKeyboardVisible,
  canRepositionCaret,
  canAutoHideOnEnter,
} = useVirtualKeyboard();

onMounted(() => {
  useTitle("Keyboard");
  useMenu("admin");
  useSubmenu(false);
  useTopBar(true);

  //Hide keyboard on this screen when main keyboard is up
  $bus.on("keyboard", onKeyboardEvent);
});

onUnmounted(() => {
  $bus.off("keyboard", onKeyboardEvent);
});

function onKeyboardEvent(data) {
  if (typeof data == "object") {
    if (typeof data.visible != "undefined") {
      isKeyboardVisible.value = !data.visible;
    }
  }
}

function keypress(data) {
  //console.log(data.char);
  if (!data.char) {
    return;
  }
  lastKey.value = data.char;
  if (data.char.length > 1 && (data.char[0] == "\\" || data.char[0] == ":")) {
    switch (data.char) {
      case ":backspace":
      case "\\backspace": //Backspace
        if (text.value.length > 0) {
          text.value = text.value.substring(0, text.value.length - 1);
        }
        break;
    }
  }
  else {
    text.value += data.char;
  }
}

function keydown(data) {
  if (!data.char) {
    return;
  }
  currentKey.value = data.char;
}

function keyup(data) {
  if (!data.char) {
    return;
  }
  currentKey.value = "";
}

function goBack() {
  try {
    $router.push({ name: "Admin" });
  }
  catch (err) {
    dialog(err);
  }
}

onVirtualKeyPress((e) => {
  console.log("[keyboard] last key", e)
  lastKey.value = e.char;
});

onVirtualKeyDown((e) => {
  console.log("[keyboard] key down", e)
  isKeyDown.value = true;
});

onVirtualKeyUp((e) => {
  console.log("[keyboard] key up", e)
  isKeyDown.value = false;
});

onVirtualKeyboardShow((e) => {
  
});

onVirtualKeyboardHide((e) => {
  isKeyDown.value = false;
});
</script>

<template>
  <div class="relative min-h-screen dark:bg-gray-900 dark:text-white p-5">

    <!-- Header -->
    <div class="w-full mx-auto flex items-center justify-between">
      <h2 class="text-lg font-bold dark:text-white">{{ $t('admin.keyboard_title') }}</h2>
      <div class="flex flex-row justify-items-strech space-x-2">
        <span class="ml-1 mr-1 mt-2">
          <i class="fa fa-keyboard text-red-600 mt-1 animate-bounce" v-if="!isKeyboardEnabled || !isKeyboardReady"></i>
          <i class="fa fa-keyboard text-yellow-300 mt-1" v-else-if="isKeyDown"></i>
          <i class="fa fa-keyboard text-blue-400 mt-1" v-else-if="!isKeyboardVisible"></i>
          <i class="fa fa-keyboard text-green-400 mt-1" v-else-if="isKeyboardVisible"></i>
          <i class="fa fa-keyboard text-slate-400 mt-1" v-else></i>
        </span>
        <Button type="secondary" @click="goBack()">{{ $t('generic.back') }}</Button>
      </div>
    </div>

    <!-- Content -->
    <div class="grid place-items-center flex flex-col gap-4 mt-2">

      <!-- First row -->
      <div class="grid grid-cols-4 gap-4 w-full mt-2">
        <div class="w-full col-span-2">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Test character set</label>
          <TextBox v-model="text1" placeholder="Type something..." class="w-full" />
        </div>
        <div class="w-full">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Last key press</label>
          <TextBox v-model="lastKey" placeholder="--" class="w-full" :readonly="true" :enabled="false"></TextBox>
        </div>
        <div class="w-full">
          <Label>Keyboard type</Label>
          <DropDown placeholder="Select" :options="keyboardTypes" v-model="keyboardType" :enabled="true" />
        </div>
        <div class="w-full">
        </div>
      </div>

      <!-- Second row -->
      <div class="grid grid-cols-4 gap-4 w-full mt-0">
        <div class="w-full col-span-1">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Switch to another</label>
          <TextBox v-model="text2" placeholder="Type..." class="w-full" />
        </div>
        <div class="w-full col-span-2">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Switch to autocomplete</label>
          <AutocompleteTextBox v-model="search" placeholder="Search something..." class="w-full" :resizable="false" />
        </div>
        <div class="w-full col-span-1">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Unfocus</label>
          <DropDown placeholder="Please select" class="w-full" />
        </div>
      </div>

      <div class="grid grid-cols-4 gap-4 w-full mt-2">
        <div class="w-full col-span-3">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Test character set with custom caret and text mirror</label>
          <TextBoxTouchFriendly v-model="text3" placeholder="Type something..." class="w-full" />
        </div>
        <div class="w-full col-span-1">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Numeric</label>
          <TextBox v-model="text4" type="numeric" placeholder="Type..." class="w-full" />
        </div>
      </div>

      <div class="grid grid-cols-4 gap-4 w-full mt-2">
        <div class="w-full col-span-4">
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Text area - multiline text</label>
          <TextAreaTouchFriendly v-model="text5" rows="10" placeholder="Type something..." class="w-full" />
        </div>
      </div>

    </div>

    <div class="keyboard-container border rounded-lg shadow-xl" v-if="isKeyboardVisible">
      <!-- <KeyboardMacbook class="keyboard-inner" @keypress="keypress" @keyup="keyup" @keydown="keydown"></KeyboardMacbook> -->
    </div>

  </div>
</template>

<style scoped>
.keyboard-container {
  display: block;
  position: fixed;
  left: 10%;
  bottom: 0;
  z-index: 10;
  background: white;
}

.keyboard-inner {
}
</style>
